"use client";

import { Button } from "@/components/tailwind/ui/button";
import { Input } from "@/components/tailwind/ui/input";
import { Label } from "@/components/tailwind/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tailwind/ui/tabs";
import { setCookie } from "cookies-next";
import { createUserWithEmailAndPassword, getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { Timestamp, addDoc, collection, doc, getDoc, getFirestore, setDoc } from "firebase/firestore";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { db } from "../../../../firebase";

export default function AuthPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>("login");

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Check if user is already logged in
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userRef = doc(db, "userCollections", user.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        const username = userData?.username;

        if (username) {
          router.push(`/${username}`);
        }
      }
    });

    return () => unsubscribe();
  }, [router]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "signup") {
      setActiveTab("signup");
    }
  }, [searchParams]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const auth = getAuth();
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      const user = result.user;

      const db = getFirestore();

      // Create a new folder named "General"
      const newFolderRef = await addDoc(collection(db, "folders"), {
        name: "General",
        documents: [],
        createdAt: new Date(),
        owner: user.uid,
      });

      // Create user collection with the new folder
      await setDoc(doc(db, "userCollections", user.uid), {
        username: username,
        email: email,
        createdAt: Timestamp.now(),
        folders: [newFolderRef.id], // Add the new folder ID to user's folders
      });

      setCookie("token", token, { maxAge: 60 * 60 * 24 }); // 1 day
      router.push(`/${username}`);
    } catch (error) {
      if (error instanceof Error) {
        alert(`❌ ${error.message}`);
        console.error("Sign-up error:", error.message);
      } else {
        alert("❌ An unexpected error occurred.");
        console.error("Sign-up error:", error);
      }
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const auth = getAuth();
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();

      setCookie("token", token, { maxAge: 60 * 60 * 24 }); // 1 day

      const userRef = doc(db, "userCollections", result.user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const username = userData?.username;

      if (username) {
        router.push(`/${username}`);
      } else {
        alert("User data is missing username.");
      }
    } catch (error) {
      if (error instanceof Error) {
        alert(`❌ ${error.message}`);
        console.error("Sign-up error:", error.message);
      } else {
        alert("❌ An unexpected error occurred.");
        console.error("Sign-up error:", error);
      }
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side - Illustration/Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-b from-gray-50 to-white p-12 items-center justify-center">
        <div className="max-w-md">
          <Link href="/" className="flex items-center gap-2 mb-8">
            {/* <div className="w-10 h-10 rounded-lg bg-bilby-900 flex items-center justify-center">
              <span className="font-bold text-white text-2xl">B</span>
            </div> */}
            <img src="/images/logo.svg" alt="Bilby Logo" className="w-10 h-10" />
            <span className="text-2xl font-bold">Bilby</span>
          </Link>
          <h1 className="text-3xl font-bold mb-6">Transform your note-taking experience</h1>
          <p className="text-gray-500 mb-8">
            Join thousands of creators, students, and professionals who use Bilby to organize their thoughts and boost
            productivity.
          </p>
          <div className="aspect-square max-w-sm rounded-lg bg-gray-100 flex items-center justify-center">
            <img
              src="/placeholder.svg?height=300&width=300"
              alt="Bilby interface illustration"
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Right side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-4 lg:hidden">
            <div className="w-8 h-8 rounded-lg bg-bilby-900 flex items-center justify-center">
              <span className="font-bold text-white text-xl">B</span>
            </div>
            <span className="text-xl font-bold">Bilby</span>
          </div>

          <h2 className="text-2xl font-bold mb-6 lg:text-3xl">
            {activeTab === "login" ? "Welcome back" : "Create your account"}
          </h2>

          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <Link href="/auth/forgot-password" className="text-xs text-blue-600 hover:underline">
                        Forgot password?
                      </Link>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    Login
                  </Button>
                  <Button variant="outline" className="w-full" disabled>
                    Login with Google (coming soon)
                  </Button>
                </div>
                <div className="mt-4 text-center text-sm">
                  Don&apos;t have an account?{" "}
                  <a href="/sign-up" className="underline underline-offset-4">
                    Sign up
                  </a>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="email">Username</Label>
                    <Input
                      id="username"
                      type="text"
                      placeholder="your username"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    Sign Up
                  </Button>
                  <Button variant="outline" className="w-full" disabled>
                    Sign up with Google (coming soon)
                  </Button>
                </div>
                <div className="mt-4 text-center text-sm">
                  Already have an account?{" "}
                  <a href="/log-in" className="underline underline-offset-4">
                    Sign in
                  </a>
                </div>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-center text-sm text-gray-500 mt-6">
            By continuing, you agree to Bilby's
            <Link href="#" className="text-blue-600 hover:underline mx-1">
              Terms of Service
            </Link>
            and
            <Link href="#" className="text-blue-600 hover:underline mx-1">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
